import React from "react";
import formatDate from "@ui/i18n/formatDate";
import { uniq } from "diagnostics-lib/array";
import type { Reporting } from "../../../api/useReporting";

export type ReportingErrors = {
  projectsWithoutPublishedAnalysesCount: number;
  projectsWithManyPublishedAnalysesCount: number;
  projectsWithManyPublishedAnalysesLog: string | null;
};

export const EMPTY_ERRORS = {
  projectsWithoutPublishedAnalysesCount: 0,
  projectsWithManyPublishedAnalysesCount: 0,
  projectsWithManyPublishedAnalysesLog: null,
};

export function useReportingErrors(reporting: Reporting): ReportingErrors {
  const errors = React.useMemo(() => {
    if (reporting.periods.length === 0) return EMPTY_ERRORS;

    const projectsWithoutPublishedAnalyses = reporting.periodAnalysisCount.filter(
      ({ count }) => count === 0,
    );

    const projectsWithManyPublishedAnalyses = reporting.periodAnalysisCount.filter(
      ({ count }) => count > 1,
    );

    const logs = projectsWithManyPublishedAnalyses.reduce<Record<string, string[]>>(
      (acc, { projectId, period }) => {
        const projectName = reporting.projectById[projectId]?.name;
        if (!projectName) return acc;
        acc[period as string] = [...(acc[period as string] || []), projectName];
        return acc;
      },
      {},
    );

    return {
      projectsWithoutPublishedAnalysesCount: projectsWithoutPublishedAnalyses.length,
      projectsWithManyPublishedAnalysesCount: uniq(
        projectsWithManyPublishedAnalyses.map(({ projectId }) => projectId),
      ).length,
      projectsWithManyPublishedAnalysesLog: Object.entries(logs)
        .flatMap(
          ([period, projects]) =>
            `${formatDate(period, "month-date", null)} : ${projects.join(", ")}`,
        )
        .join("\n"),
    };
  }, [reporting]);

  return errors;
}
